// idb.ts - IndexedDB helper
import { openDB, IDBPDatabase } from "idb";
interface DBStructure {
  assets: any;
  inspectionPointsMapping: { tempId: string | number; realId: number };
  images: { id: string; blob: Blob };
}

let dbInstance: IDBPDatabase<DBStructure> | null = null;

async function openConnection(): Promise<IDBPDatabase<DBStructure>> {
  return openDB<DBStructure>("asset-store", 4, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("assets")) {
        db.createObjectStore("assets", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("inspectionPointsMapping")) {
        db.createObjectStore("inspectionPointsMapping", { keyPath: "tempId" });
      }
      if (!db.objectStoreNames.contains("images")) {
        db.createObjectStore("images", { keyPath: "id" });
      }
    },
  });
}

export async function getDb(): Promise<IDBPDatabase<DBStructure>> {
  try {
    if (!dbInstance) {
      dbInstance = await openConnection();
    } else {
      // Quick test to see if it's still alive
      dbInstance.transaction("assets");
    }
    return dbInstance;
  } catch (err) {
    console.warn("IndexedDB connection lost. Reinitializing...");
    dbInstance = await openConnection();
    return dbInstance;
  }
}

export async function saveAsset(asset: any) {
  const db = await getDb();
  await db.put("assets", asset);
}

export async function getOfflineAssets() {
  const db = await getDb();
  return await db.getAll("assets");
}

export async function getOfflineAssetById(id: number | string) {
  const db = await getDb();
  return await db.get("assets", id);
}

export async function deleteAsset(id: number) {
  const db = await getDb();
  await db.delete("assets", id);
}

export async function updateLocalAsset(id: number, updatedAsset: any) {
  const db = await getDb();
  await db.put("assets", updatedAsset, id);
}

export async function replaceTempIds(localid: number, serverAsset: any) {
  const db = await getDb();
  const localAsset = await db.get("assets", localid);

  serverAsset.modules.forEach((serverModule: any, index: number) => {
    const localModule = localAsset.modules.find(
      (m: any) => m.id === `temp-module-${index}`
    );
    if (localModule) {
      serverModule.points.forEach(async (serverPoint: any, pIndex: number) => {
        const localPoint = localModule.points.find(
          (p: any) => p.id === `temp-point-${index}-${pIndex}`
        );
        if (localPoint) {
          await db.put("inspectionPointMapping", {
            tempId: localPoint.id,
            realId: serverPoint.id,
          });
          localPoint.id = serverPoint.id; // Replace temp point ID
        }
      }); // Replace temp module ID
    }
  });

  return serverAsset;
}

export async function getInspectionPointMapping(
  localId: number | string
): Promise<number> {
  try {
    const db = await getDb();
    const mapping = await db.get("inspectionPointsMapping", localId);
    return mapping ? mapping.realId : -1;
  } catch (err) {
    console.error("IndexedDB failure in getInspectionPointMapping:", err);
    throw new Error("indexeddb-lost-connection");
  }
}

export async function saveInspectionPointMapping(
  localId: number | string,
  realId: number
) {
  const db = await getDb();
  await db.put("inspectionPointsMapping", { tempId: localId, realId: realId });
}

export async function putImage(id: string, blob: Blob) {
  try {
    const db = await getDb();
    await db.put('images', { id: id, blob: blob})
  } catch (e) {
    console.log('unable to store image in IDB')
  }
}

export async function getImage(id: string) {
  try {
    const db = await getDb();
    return await db.get("images", id);
  } catch (e) {
    console.error("IndexedDB failure in images:", e);
  }
}

export async function deleteImage(id: string) {
  try {
    const db = await getDb();
    await db.delete("images", id);
  } catch (e) {
    console.error(`Failed to delete image with ID ${id} from IndexedDB:`, e);
  }
}
